<template>
  <div
    class="px-5 light-box-bg"
    :class="$dm('bg-slate-50', 'dark:bg-slate-900')"
  >
    <div class="flex items-center justify-between mb-3">
      <div
        class="max-w-xs"
        :class="$dm('text-black-900', 'dark:text-slate-25')"
      >
        <div class="text-base leading-5 font-medium mb-1">
          {{ availabilityText }}
        </div>
        <div
          class="text-xs leading-4 mt-1"
          :class="$dm('text-black-700', 'dark:text-slate-50')"
        >
          {{
            isAssistantAttached
              ? $t('REPLY_TIME.IN_A_FEW_SECONDS')
              : replyWaitMessage
          }}
        </div>
      </div>
      <available-agents v-if="isOnline" :agents="availableAgents" />
    </div>
    <custom-button
      class="font-medium"
      style="border-radius: 9px;"
      block
      :bg-color="widgetColor"
      :text-color="textColor"
      @click="startConversation"
      @keyup.enter="startConversation"
    >
      {{ startConversationText }}
    </custom-button>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { getContrastingTextColor } from '@chatwoot/utils';
import nextAvailabilityTime from 'widget/mixins/nextAvailabilityTime';
import AvailableAgents from 'widget/components/AvailableAgents.vue';
import CustomButton from 'shared/components/Button';
import configMixin from 'widget/mixins/configMixin';
import availabilityMixin from 'widget/mixins/availability';
import assistantMixin from 'widget/mixins/assistantMixin';
import darkMixin from 'widget/mixins/darkModeMixin.js';
import conversationMixin from 'widget/mixins/conversationMixin.js';
import { IFrameHelper } from '../helpers/utils';

export default {
  name: 'TeamAvailability',
  components: {
    AvailableAgents,
    CustomButton,
  },
  mixins: [
    configMixin,
    nextAvailabilityTime,
    availabilityMixin,
    darkMixin,
    assistantMixin,
    conversationMixin,
  ],
  props: {
    availableAgents: {
      type: Array,
      default: () => {},
    },
    hasConversation: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    ...mapGetters({
      widgetColor: 'appConfig/getWidgetColor',
      currentUser: 'contacts/getCurrentUser',
    }),
    textColor() {
      return getContrastingTextColor(this.widgetColor);
    },
    isOnline() {
      const { workingHoursEnabled } = this.channelConfig;
      const anyAgentOnline = this.availableAgents.length > 0;

      if (workingHoursEnabled) {
        return this.isInBetweenTheWorkingHours;
      }
      return anyAgentOnline;
    },
    isContinueConversation() {
      return this.hasConversation && !this.hasShowConversationHistory;
    },
    availabilityText() {
      if (this.isOnline || window.hooryWebChannel.assistantType)
        return this.$t('TEAM_AVAILABILITY.ONLINE');
      return this.$t('TEAM_AVAILABILITY.OFFLINE');
    },
    shouldForceLogin() {
      const isUserLoggedIn =
        this.currentUser &&
        (this.currentUser.email || this.currentUser.identifier);
      return !isUserLoggedIn && window.hooryWebChannel.enforceIdentityUrl;
    },
    startConversationText() {
      if (this.shouldForceLogin) return this.$t('LOGIN_TO_START_CONVERSATION');

      return this.isContinueConversation
        ? this.$t('CONTINUE_CONVERSATION')
        : this.$t('START_CONVERSATION');
    },
  },
  methods: {
    openLoginUrl() {
      IFrameHelper.sendMessage({
        event: 'openLink',
        data: {
          url: window.hooryWebChannel.enforceIdentityUrl,
          target: '_self',
        },
      });
    },
    startConversation() {
      if (this.shouldForceLogin) {
        this.openLoginUrl();
        return;
      }

      this.cleanCurrentConversation();

      if (this.isContinueConversation) {
        this.$store.dispatch('conversationAttributes/getAttributes');

        this.$store.dispatch('conversation/fetchCurrentConversationMessages', {
          conversation_id: this.conversationParams.id,
        });
      }

      this.$emit('start-conversation', this.isContinueConversation);
    },
  },
};
</script>
<style lang="scss" scoped>
.light-box-bg {
  position: fixed;
  width: 100%;
  bottom: 0;
  padding-bottom: 43px;
  padding-top: 12px;
}
</style>
